import { useEffect, useState } from "react";
import { useHref } from "react-router-dom";
import { ClientEndpointCadenceUnit, ClientEndpointType, DrakewellClientEndpoint } from "@app/shared";
import { useModel } from "@app/shared/src/hooks";
import { post } from "@app/shared/src/api/api_client";
import { CheckboxInput, Input, InputLabel, InputRow, Select } from "@app/dashboard/src/components/shared/inputs";
import { apiDocsHrefForEndpoint } from "../EndpointApiDocs/specRegistry";
import { HoverTooltip } from "@app/dashboard/src/components/shared/HoverTooltip";
import { toastError, toastSuccess } from "@app/dashboard/src/helpers/toastHelpers";
import { Button, ButtonVariants } from "@app/shared/src/components/Button";
import { ClientEndpointEditorProps } from "./ClientEndpointEditor";

type EditDrakewellClientEndpointProps = {} & ClientEndpointEditorProps;

// Enough detail to attempt a test: a plausible URL. Precise URL validation happens on the device
// (test + save both reject bad input) — this just gates the button.
const canTestEndpoint = (endpoint: Partial<DrakewellClientEndpoint>) =>
    !!endpoint.endpointUrl && /^https?:\/\//i.test(endpoint.endpointUrl.trim());

// The send cadence is a unit plus a count (or a time of day, for Daily) — the shape the editor always
// offered. It used to be stored as a 6-field cron expression that this component generated and parsed
// back, which meant a cronstrue dependency for the description, a Custom escape hatch nobody needed,
// and a round-trip that silently fell back to Custom whenever an equivalent-but-differently-written
// expression came back from the server. The fields below ARE the stored value now.
const CADENCE_OPTIONS = [
    { value: `${ClientEndpointCadenceUnit.Seconds}`, label: "Every N seconds" },
    { value: `${ClientEndpointCadenceUnit.Minutes}`, label: "Every N minutes" },
    { value: `${ClientEndpointCadenceUnit.Hours}`, label: "Every N hours" },
    { value: `${ClientEndpointCadenceUnit.Daily}`, label: "Daily at a time" },
];

const UNIT_NOUNS: Record<number, string> = {
    [ClientEndpointCadenceUnit.Seconds]: "second",
    [ClientEndpointCadenceUnit.Minutes]: "minute",
    [ClientEndpointCadenceUnit.Hours]: "hour",
};

const MAX_INTERVAL_SECONDS = 24 * 60 * 60;
const UNIT_SECONDS: Record<number, number> = {
    [ClientEndpointCadenceUnit.Seconds]: 1,
    [ClientEndpointCadenceUnit.Minutes]: 60,
    [ClientEndpointCadenceUnit.Hours]: 3600,
};

const pad2 = (n: number) => `${n}`.padStart(2, "0");

// Mirrors CadenceScheduleHelper.Humanize/IsValid so the editor shows the same sentence and the same
// rejection the device and cloud will apply on save.
const describeCadence = (unit: number, interval: number, dailyMinuteUtc: number): { human?: string; error?: string } => {
    if (unit === ClientEndpointCadenceUnit.Daily) {
        if (dailyMinuteUtc < 0 || dailyMinuteUtc >= 24 * 60) return { error: "Daily send time must be between 00:00 and 23:59 UTC." };
        return { human: `day at ${pad2(Math.floor(dailyMinuteUtc / 60))}:${pad2(dailyMinuteUtc % 60)} UTC` };
    }
    const noun = UNIT_NOUNS[unit];
    if (!noun) return { error: "Choose a send cadence." };
    if (!Number.isFinite(interval) || interval < 1) return { error: "Send interval must be at least 1." };
    if (interval * (UNIT_SECONDS[unit] ?? 1) > MAX_INTERVAL_SECONDS) {
        return { error: "Send interval cannot exceed 24 hours — use the daily cadence instead." };
    }
    return { human: interval === 1 ? noun : `${interval} ${noun}s` };
};

type CadenceValue = { cadenceUnit: number; cadenceInterval: number; cadenceDailyMinuteUtc: number };

const CadenceInput: React.FC<{ value: CadenceValue; onChange: (next: CadenceValue) => void }> = ({ value, onChange }) => {
    const unit = value.cadenceUnit ?? ClientEndpointCadenceUnit.Minutes;
    const interval = value.cadenceInterval ?? 5;
    const dailyMinute = value.cadenceDailyMinuteUtc ?? 8 * 60;

    // Seed defaults for a brand-new endpoint so the schedule is never left blank.
    useEffect(() => {
        if (value.cadenceUnit == null || value.cadenceInterval == null || value.cadenceDailyMinuteUtc == null) {
            onChange({ cadenceUnit: unit, cadenceInterval: interval, cadenceDailyMinuteUtc: dailyMinute });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const info = describeCadence(unit, interval, dailyMinute);
    const showInterval = unit !== ClientEndpointCadenceUnit.Daily;

    return (
        <InputRow>
            <InputLabel>Send Schedule</InputLabel>
            <div className="flex flex-wrap items-center gap-2">
                <Select
                    className="w-52"
                    value={`${unit}`}
                    options={CADENCE_OPTIONS}
                    onChange={(e) => onChange({ cadenceUnit: parseInt(e.target.value), cadenceInterval: interval, cadenceDailyMinuteUtc: dailyMinute })}
                />
                {showInterval &&
                    <span className="flex items-center gap-2">
                        <span className="text-sm text-muted-body-text">every</span>
                        <Input type="number" min={1} className="w-20" value={`${interval}`}
                            onChange={(e) => onChange({ cadenceUnit: unit, cadenceInterval: Math.max(1, parseInt(e.target.value) || 1), cadenceDailyMinuteUtc: dailyMinute })} />
                        <span className="text-sm text-muted-body-text">{UNIT_NOUNS[unit]}s</span>
                    </span>}
                {unit === ClientEndpointCadenceUnit.Daily &&
                    <span className="flex items-center gap-2">
                        <span className="text-sm text-muted-body-text">at</span>
                        <Input type="time" className="w-32" value={`${pad2(Math.floor(dailyMinute / 60))}:${pad2(dailyMinute % 60)}`}
                            onChange={(e) => {
                                const [h, m] = e.target.value.split(":");
                                const minutes = (parseInt(h) || 0) * 60 + (parseInt(m) || 0);
                                onChange({ cadenceUnit: unit, cadenceInterval: interval, cadenceDailyMinuteUtc: minutes });
                            }} />
                        <span className="text-xs text-muted-body-text">UTC</span>
                    </span>}
            </div>
            {info.human && <div className="text-sm text-muted-body-text mt-1">Sends a batch every {info.human}.</div>}
            {info.error && <div className="text-sm text-red-500 mt-1">{info.error}</div>}
        </InputRow>
    );
};

// Drakewell inherits the JSON endpoint fields (minus heartbeat) and adds a send cadence. The
// Test button lives in the dialog footer (see DrakewellTestFooter) — firmware/offline only, since
// the /api/clientendpoint/test route lives on the device.
export const EditDrakewellClientEndpoint: React.FC<EditDrakewellClientEndpointProps> = (props) => {
    // jsonVersion is inherited from the JSON endpoint model but is NOT a Drakewell knob: the record
    // shape is pinned by the versioned Drakewell wire contract (schemaVersion in each batch), so the
    // editor doesn't expose it — the default is kept for model compatibility only.
    const endpointWithDefaults = !!!props.endpoint?.id ? { ...props.endpoint, jsonVersion: 2 } : props.endpoint;
    const [endpoint, helper] = useModel(DrakewellClientEndpoint, endpointWithDefaults as DrakewellClientEndpoint);
    const onChange = (newEndpoint: Partial<DrakewellClientEndpoint>) => {
        props.onChange({ ...newEndpoint as DrakewellClientEndpoint, endpointType: ClientEndpointType.Drakewell });
    };

    // The cadence controls have to write through this editor's OWN model, not just notify the parent.
    // Every other field goes through helper.bindingsFor, which calls the model's setter and THEN the
    // callback; a control that only calls the callback leaves this editor's `endpoint` stale, so it
    // re-renders with the old value and reads as a frozen input. replaceModel + onChange is the same pair
    // handlerFor performs internally, done once so a unit change and its dependent fields land together
    // rather than as separate updates that would each rebuild from the same stale snapshot.
    const applyCadence = (next: CadenceValue) => {
        const updated = { ...endpoint, ...next } as DrakewellClientEndpoint;
        helper.replaceModel(updated);
        onChange(updated);
    };
    // Resolved through the shared spec registry, which picks the document for this endpoint type and
    // handles hosts that don't bundle the docs page: WebConfig gets an absolute dashboard URL via
    // docsOrigin, the dashboard gets an in-app path under its basename (works behind the device proxy).
    // Returns undefined when there is nowhere to link, in which case the row renders nothing.
    const appBaseHref = useHref("/");
    const docsHref = apiDocsHrefForEndpoint(
        { endpointType: ClientEndpointType.Drakewell },
        { docsOrigin: props.docsOrigin, hostsDocsPage: !props.offlineMode, appBaseHref }
    );


    return (
        <>
            {docsHref && (
                <InputRow>
                    <a href={docsHref} target="_blank" rel="noopener noreferrer" className="text-link-page underline text-sm">
                        View the Drakewell Push API documentation (payload schema, auth, retry semantics) ↗
                    </a>
                </InputRow>
            )}
            <InputRow><Input label="Endpoint Name" {...helper.bindingsFor("endpointName", onChange)} /></InputRow>
            <InputRow><Input label="Endpoint URL" {...helper.bindingsFor("endpointUrl", onChange)} /></InputRow>
            <InputRow><CheckboxInput label="Include Target History" {...helper.bindingsFor("includeTargetHistory", onChange)} /></InputRow>
            <InputRow><Input label="HMAC Secret" placeholder="HMAC shared secret key" {...helper.bindingsFor("sharedSecretKey", onChange)} /></InputRow>
            <InputRow><Input label="Custom Auth Header" placeholder="MY_AUTH_HEADER=authValue" {...helper.bindingsFor("customAuthHeader", onChange)} /></InputRow>
            <CadenceInput
                value={{
                    // Defaults mirror the C# model's, so a brand-new endpoint renders a sensible cadence
                    // before the seeding effect writes one back.
                    cadenceUnit: endpoint.cadenceUnit ?? ClientEndpointCadenceUnit.Minutes,
                    cadenceInterval: endpoint.cadenceInterval ?? 5,
                    cadenceDailyMinuteUtc: endpoint.cadenceDailyMinuteUtc ?? 8 * 60,
                }}
                onChange={applyCadence}
            />
        </>
    );
}

type DrakewellTestFooterProps = { endpoint: Partial<DrakewellClientEndpoint> };

// Footer-mounted test control for the Drakewell editor dialog. POSTs an empty batch — or one seeded
// with sample targets — using the current form values, so the URL/auth can be verified before saving.
// Firmware/offline only, since the /api/clientendpoint/test route lives on the device. Results are
// surfaced as toasts rather than inline.
export const DrakewellTestFooter: React.FC<DrakewellTestFooterProps> = ({ endpoint }) => {
    const [includeTestTargets, setIncludeTestTargets] = useState(false);

    const canTest = canTestEndpoint(endpoint);

    // The failure "message" carries the remote server's status + response body. Show it in full in a
    // persistent (non-auto-dismissing) toast with a scrollable block, since that response is the whole
    // point of running a test — it must stay on screen long enough to read/copy.
    const showFailure = (response: string) =>
        toastError(
            "Test failed",
            <div className="mt-1 max-w-md">
                <div className="text-sm mb-1">Response from the endpoint:</div>
                <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-words text-xs bg-black/5 rounded p-2 select-text">{response || "(no response body)"}</pre>
            </div>,
            Infinity
        );

    const runTest = async () => {
        try {
            // Cast via any to mirror ClientEndpointTable.instantiateEndpoint — the class ctor expects
            // the interface (lastModified: string) but the live model carries a Date.
            const payload = new DrakewellClientEndpoint({ ...endpoint, endpointType: ClientEndpointType.Drakewell } as any);
            const result = await post<{ success: boolean; message: string }>(
                `/api/clientendpoint/test?includeTestTargets=${includeTestTargets}`,
                payload
            );
            if (!result) showFailure("No response from the device.");
            else if (result.success) toastSuccess("Test sent", result.message);
            else showFailure(result.message);
        } catch (e) {
            showFailure(e instanceof Error ? e.message : String(e));
        }
    };

    return (
        <div className="flex items-center gap-3">
            <HoverTooltip hoverable={
                <Button variant={ButtonVariants.Secondary} disabled={!canTest} onClick={runTest}>
                    Test
                </Button>
            }>
                Sends a test message using the values entered in this dialog right now — including unsaved
                changes.
            </HoverTooltip>
            <div className="text-xs">
                <CheckboxInput
                    label="Include test targets"
                    checked={includeTestTargets}
                    onChange={(e) => setIncludeTestTargets(e.currentTarget.checked)}
                />
            </div>
            {!canTest &&
                <span className="text-xs text-gray-500">Enter a valid endpoint URL to test.</span>}
        </div>
    );
}
